import { RiskScore } from "@tfg/db"
import { CountrySelectorCustomRender, FilterOptions } from "@tfg/ui"
import { useTranslation } from "react-i18next"

import { useHeaderControlsContext } from "~/components/contexts/useHeaderControlsContext"
import {
  businessRelationshipDtoTypeEnum,
  FindContactRequestFiltersDto,
  findTags,
  useFindTags,
} from "~/gen/api"
import { useBusinessPartnerMultiSelectFilterOptionResolver } from "~/pages/Brand/Suppliers/SuppliersRepository/hooks/useBusinessPartnerMultiSelectFilterOptionResolver"
import { COUNTRIES } from "~/utils/countries"

export const useBuildBusinessPartnerFilters = () => {
  const { t } = useTranslation(["traceability", "suppliers"])
  const { filters, setFilters } = useHeaderControlsContext()
  const { data: tagsData, isFetching: isTagsLoading } = useFindTags()
  const businessPartnerMultiSelectFilterOptionResolver =
    useBusinessPartnerMultiSelectFilterOptionResolver()

  const filterOptions: FilterOptions<FindContactRequestFiltersDto>[] = [
    {
      label: t("supplierSettings.filters.status"),
      key: "active",
      type: "multiSelect",
      options: [
        { label: t("contactTable.status.active"), value: "true" },
        { label: t("contactTable.status.inactive"), value: "false" },
      ],
    },
    {
      label: t("contactTable.columns.type.name"),
      key: "type",
      type: "multiSelect",
      options: [
        {
          label: t("contactModal.labels.companyRelationshipType.AGENT"),
          value: businessRelationshipDtoTypeEnum.AGENT,
        },
        {
          label: t("contactModal.labels.companyRelationshipType.MANUFACTURER"),
          value: businessRelationshipDtoTypeEnum.MANUFACTURER,
        },
        {
          label: t("contactModal.labels.companyRelationshipType.SUPPLIER"),
          value: businessRelationshipDtoTypeEnum.SUPPLIER,
        },
        {
          label: t(
            "contactModal.labels.companyRelationshipType.RAW_MATERIAL_SUPPLIER"
          ),
          value: businessRelationshipDtoTypeEnum.RAW_MATERIAL_SUPPLIER,
        },
        {
          label: t("contactModal.labels.companyRelationshipType.TRADER"),
          value: businessRelationshipDtoTypeEnum.TRADER,
        },
        {
          label: t("contactModal.labels.companyRelationshipType.BRAND"),
          value: businessRelationshipDtoTypeEnum.BRAND,
        },
        {
          label: t("contactModal.labels.companyRelationshipType.OTHER"),
          value: businessRelationshipDtoTypeEnum.OTHER,
        },
      ],
    },
    {
      label: t("contactTable.columns.location.name"),
      key: "country",
      type: "multiSelect",
      options: Object.entries(COUNTRIES).map(([_, value]) => ({
        label: value.countryName,
        value: value.code,
      })),
      customRender: CountrySelectorCustomRender(COUNTRIES),
    },
    {
      label: t("supplierSettings.filters.tags"),
      key: "tagNames",
      type: "async-multi-select",
      options:
        tagsData?.data?.tags.map((tag: { name: string }) => ({
          label: tag.name,
          value: tag.name,
        })) ?? [],
      asyncRefetch: async (searchQuery?: string) => {
        const result = await findTags()
        const tags = result.data?.tags ?? []

        if (searchQuery) {
          const query = searchQuery.toLowerCase()
          return tags
            .filter((tag) => tag.name.toLowerCase().includes(query))
            .map((tag) => ({
              label: tag.name,
              value: tag.name,
            }))
        }

        return tags.map((tag) => ({
          label: tag.name,
          value: tag.name,
        }))
      },
      isAsyncLoading: isTagsLoading,
    },
    {
      ...businessPartnerMultiSelectFilterOptionResolver<"productionSites">({
        fieldLabel: t("supplierSettings.filters.productionSite"),
        fieldKey: "productionSiteIds",
        key: "productionSites",
        resolveLabel: (site) => site.name,
        resolveValue: (site) => site.id,
      }),
    },
    {
      ...businessPartnerMultiSelectFilterOptionResolver<"codes">({
        fieldLabel: t("contactTable.columns.code.name"),
        fieldKey: "supplierCode",
        key: "codes",
        resolveLabel: (code) => code,
        resolveValue: (code) => code,
      }),
    },
    {
      ...businessPartnerMultiSelectFilterOptionResolver<"users">({
        fieldLabel: t("contactTable.columns.contactEmail.name"),
        fieldKey: "email",
        key: "users",
        resolveLabel: (user) => user.email,
        resolveValue: (user) => user.email,
      }),
    },
    {
      ...businessPartnerMultiSelectFilterOptionResolver<"users">({
        fieldLabel: t("contactTable.columns.contactName.name"),
        fieldKey: "name",
        key: "users",
        resolveLabel: (user) => user.name,
        resolveValue: (user) => user.name,
      }),
    },
    {
      ...businessPartnerMultiSelectFilterOptionResolver<"organizations">({
        fieldLabel: t("contactTable.columns.company.name"),
        fieldKey: "organization",
        key: "organizations",
        resolveLabel: (organization) => organization.name,
        resolveValue: (organization) => organization.name,
      }),
    },
    {
      label: t("contactTable.columns.activity.name"),
      key: "lastLogin",
      type: "dateRange",
      options: [],
    },
    {
      label: t("contactTable.columns.reminder.name"),
      key: "lastReminderDate",
      type: "dateRange",
      options: [],
    },
    {
      label: t("suppliers:risk.table.collaborationRisk.title"),
      key: "collaborationRisk",
      type: "multiSelect",
      options: [
        {
          label: t("suppliers:risk.low"),
          value: RiskScore.LOW,
        },
        {
          label: t("suppliers:risk.medium"),
          value: RiskScore.MEDIUM,
        },
        {
          label: t("suppliers:risk.high"),
          value: RiskScore.HIGH,
        },
      ],
    },
    {
      label: t("suppliers:risk.table.certificationRisk.title"),
      key: "certificateRisk",
      type: "multiSelect",
      options: [
        {
          label: t("suppliers:risk.low"),
          value: RiskScore.LOW,
        },
        {
          label: t("suppliers:risk.medium"),
          value: RiskScore.MEDIUM,
        },
        {
          label: t("suppliers:risk.high"),
          value: RiskScore.HIGH,
        },
      ],
    },
  ]

  return {
    filters,
    setFilters,
    filterOptions,
  }
}
